import React from 'react';
import CustomInputsTabContent_Events from './CustomInputsTabContent_Events';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { PlusIcon } from '@heroicons/react/20/solid';
import { SectionHeader } from '@/components/ui/section-header';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { EmptyState } from '../../../../components/ui/empty-state';

const REVERSE_INPUT_TYPE_MAPPING = {
  'Short Text Input': 'SHORT_TEXT_INPUT',
  'Long Text Input': 'LONG_TEXT_INPUT',
  'Single Selection Dropdown': 'SINGLE_SELECTION_DROPDOWN',
  'Multi Selection Dropdown': 'MULTI_SELECTION_DROPDOWN',
};

export type InputType =
  | 'SHORT_TEXT_INPUT'
  | 'LONG_TEXT_INPUT'
  | 'SINGLE_SELECTION_DROPDOWN'
  | 'MULTI_SELECTION_DROPDOWN';
export type QuestionType = 'PURCHASER' | 'ATTENDEE';

interface CustomInput {
  id: string;
  title: string;
  type: InputType | string;
  options: string[];
  required: boolean;
  questionType: QuestionType;
}

interface CustomInputsTabEventsProps {
  customInputs: CustomInput[];
  setCustomInputs: (inputs: CustomInput[]) => void;
  handleFieldFocus: (fieldName: string) => void;
  isFieldInvalid: (fieldName: string) => boolean;
  setInvalidFields?: (fields: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  showSectionHeader?: boolean;
}

function CustomInputsTab_Events(props: CustomInputsTabEventsProps) {
  const { customInputs, setCustomInputs, handleFieldFocus, isFieldInvalid } = props;

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.dataset.index || '0');
    const value = event.target.value;

    const newCustomInputs = [...customInputs];
    const newCustomInput = newCustomInputs[index];
    newCustomInput.title = value;
    setCustomInputs(newCustomInputs);
  };

  const handleTypeChange = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const index = parseInt((event.target as HTMLAnchorElement).dataset.index || '0');
    let value = (event.target as HTMLAnchorElement).dataset.value || '';

    value = REVERSE_INPUT_TYPE_MAPPING[value as keyof typeof REVERSE_INPUT_TYPE_MAPPING];

    const newCustomInputs = [...customInputs];
    const newCustomInput = newCustomInputs[index];
    newCustomInput.type = value;
    // clear options if type is not a dropdown
    if (value !== 'SINGLE_SELECTION_DROPDOWN' && value !== 'MULTI_SELECTION_DROPDOWN') {
      newCustomInput.options = [];
    }

    setCustomInputs(newCustomInputs);
  };

  const handleOptionsChange = (options: string[], index: number) => {
    const newCustomInputs = [...customInputs];
    const newCustomInput = newCustomInputs[index];
    newCustomInput.options = options;
    setCustomInputs(newCustomInputs);
  };

  const handleRequiredChange = (value: boolean, index: number) => {
    const newCustomInputs = [...customInputs];
    const newCustomInput = newCustomInputs[index];
    newCustomInput.required = value;

    setCustomInputs(newCustomInputs);
  };

  const handleQuestionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.dataset.index || '0');
    const value = event.target.value as QuestionType;

    const newCustomInputs = [...customInputs];
    const newCustomInput = newCustomInputs[index];
    newCustomInput.questionType = value;
    setCustomInputs(newCustomInputs);
  };

  const createNewCustomInput = () => {
    const newCustomInputs = [...customInputs];
    newCustomInputs.push({
      title: '',
      type: 'Select an Input Type',
      options: [],
      required: false,
      questionType: 'PURCHASER',
      id: Math.random().toString(36).substring(2, 9), // generates a random id
    });
    setCustomInputs(newCustomInputs);
  };

  const removeCustomInput = (index: number) => {
    const newCustomInputs = [...customInputs];
    newCustomInputs.splice(index, 1);
    setCustomInputs(newCustomInputs);

    // Clean up validation errors for this custom input
    if (props.setInvalidFields) {
      props.setInvalidFields((prev) => {
        const newInvalidFields = new Set(prev);

        // Remove validation errors for all custom input fields
        // Field names are: custom_fields-customInput-title-{index}, custom_fields-customInput-type-{index}, custom_fields-customInput-options-{index}
        Array.from(newInvalidFields).forEach((field) => {
          if (field.startsWith('custom_fields-customInput-')) {
            // Extract the index from field name (e.g., 'custom_fields-customInput-title-2' -> 2)
            const match = field.match(/custom_fields-customInput-(?:title|type|options)-(\d+)/);
            if (match) {
              const fieldIndex = parseInt(match[1]);

              // If this is the removed custom input, delete its validation errors
              if (fieldIndex === index) {
                newInvalidFields.delete(field);
              }
              // If this is a custom input after the removed one, update its index
              else if (fieldIndex > index) {
                newInvalidFields.delete(field);
                // Add the field back with updated index (shifted down by 1)
                const fieldType = field.includes('title')
                  ? 'title'
                  : field.includes('type')
                    ? 'type'
                    : 'options';
                newInvalidFields.add(`custom_fields-customInput-${fieldType}-${fieldIndex - 1}`);
              }
            }
          }
        });

        // If no custom inputs remain, remove all custom input-related validation errors
        if (newCustomInputs.length === 0) {
          Array.from(newInvalidFields).forEach((field) => {
            if (field.startsWith('custom_fields-')) {
              newInvalidFields.delete(field);
            }
          });
        }

        return newInvalidFields;
      });
    }
  };

  const onDragEnd = (result: any) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const newCustomInputs = [...customInputs];
    const newCustomInput = newCustomInputs[source.index];
    newCustomInputs.splice(source.index, 1);
    newCustomInputs.splice(destination.index, 0, newCustomInput);
    setCustomInputs(newCustomInputs);
  };

  return (
    <div className={`space-y-3 ${props.showSectionHeader === false ? '' : 'pr-2'}`}>
      {props.showSectionHeader !== false && (
        <SectionHeader title="Custom Input Fields Configuration" />
      )}
      <div className="space-y-3">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <div className="space-y-4">
                  {customInputs.length === 0 ? (
                    <EmptyState
                      icon={<ClipboardDocumentListIcon className="h-6 w-6 text-ink-muted" />}
                      title="No custom fields added"
                      description="Create custom input fields to collect additional information from your attendees."
                      actionLabel="Add Custom Input Field"
                      onAction={createNewCustomInput}
                    />
                  ) : (
                    customInputs.map((customInput, index) => (
                      <div key={customInput.id}>
                        <h3 className="text-base font-medium text-ink-secondary">
                          Custom Input Field #{index + 1}
                        </h3>
                        <CustomInputsTabContent_Events
                          id={customInput.id}
                          customInput={customInput}
                          index={index}
                          handleTitleChange={handleTitleChange}
                          handleTypeChange={handleTypeChange}
                          handleOptionsChange={handleOptionsChange}
                          handleRequiredChange={handleRequiredChange}
                          handleQuestionTypeChange={handleQuestionTypeChange}
                          removeCustomInput={removeCustomInput}
                          handleFieldFocus={handleFieldFocus}
                          isFieldInvalid={isFieldInvalid}
                        />
                      </div>
                    ))
                  )}
                </div>
                {provided.placeholder}
                {customInputs.length > 0 && (
                  <div className="mt-1 flex justify-end">
                    <button
                      type="button"
                      onClick={createNewCustomInput}
                      className="flex items-center space-x-1 text-ink-secondary hover:text-ink"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span className="text-sm">Add Custom Input Field</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}

export default CustomInputsTab_Events;
